import {
  Card,
  Grid,
  Button,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import DashboardNavbar from 'components/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import { useEffect, useState } from 'react';
import { getStatistics } from 'services/statistics/get-statistics';
import { getUserSessions } from 'services/statistics/get-user-sessions';
import { getGamesPlayed } from 'services/statistics/get-games-played';

// Game type display names
const GAME_TYPE_NAMES = {
  roulette: 'Roulette',
  blackjack: 'Blackjack',
  baccarat: 'Baccarat',
  slots: 'Slots',
  jetpack: 'Firework Frenzy',
  crash: 'Dragon Fortune',
  'bound-in-gold': 'Bound in Gold',
};

export default function MarketingStatistics() {
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date;
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return date;
  });
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [userSessions, setUserSessions] = useState([]);
  const [loadingUserSessions, setLoadingUserSessions] = useState(false);
  const [gamesPlayed, setGamesPlayed] = useState(null);
  const [loadingGamesPlayed, setLoadingGamesPlayed] = useState(false);

  const getMaxDateString = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };

  const getDateOnly = (date) => {
    return date.toISOString().split('T')[0];
  };

  const handleStartDateChange = (e) => {
    const selectedDate = new Date(e.target.value);
    if (e.target.value > getMaxDateString()) {
      alert('Cannot select today or future dates. Maximum date is yesterday.');
      return;
    }
    setStartDate(selectedDate);
  };

  const handleEndDateChange = (e) => {
    const selectedDate = new Date(e.target.value);
    if (e.target.value > getMaxDateString()) {
      alert('Cannot select today or future dates. Maximum date is yesterday.');
      return;
    }
    setEndDate(selectedDate);
  };

  const fetchStatistics = async () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates');
      return;
    }

    const maxDateStr = getMaxDateString();
    if (getDateOnly(startDate) > maxDateStr || getDateOnly(endDate) > maxDateStr) {
      alert('Cannot fetch statistics for today or future dates. Maximum date is yesterday.');
      return;
    }

    if (startDate >= endDate) {
      alert('Start date must be before end date');
      return;
    }

    setLoading(true);
    setLoadingUserSessions(true);
    setLoadingGamesPlayed(true);
    setError(null);

    try {
      const startISO = startDate.toISOString();
      const endISO = endDate.toISOString();

      const [statsRes, sessionsRes, gamesRes] = await Promise.all([
        getStatistics({
          startDate: startISO,
          endDate: endISO,
          includeComparison: false,
        }),
        getUserSessions({
          startDate: startISO,
          endDate: endISO,
        }),
        getGamesPlayed({
          startDate: startISO,
          endDate: endISO,
        }),
      ]);

      if (statsRes?.data) {
        setStats(statsRes.data);
      } else {
        setError(statsRes?.response?.data?.message || 'Failed to fetch statistics');
      }

      if (sessionsRes?.data) {
        setUserSessions(Array.isArray(sessionsRes.data) ? sessionsRes.data : []);
      }

      if (gamesRes?.data) {
        setGamesPlayed(gamesRes.data);
      }
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError(err?.response?.data?.message || 'Error fetching statistics');
    } finally {
      setLoading(false);
      setLoadingUserSessions(false);
      setLoadingGamesPlayed(false);
    }
  };

  useEffect(() => {
    fetchStatistics();
  }, []);

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0s';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <MDBox display='flex' justifyContent='space-between' alignItems='center' mb={2}>
            <MDTypography variant='h4' fontWeight='bold' color='dark'>
              Marketing Statistics
            </MDTypography>
          </MDBox>

          {/* Date Range Picker */}
          <Card sx={{ boxShadow: 2, borderRadius: 2, p: 2, mb: 2 }}>
            <MDBox display='flex' gap={2} alignItems='center' flexWrap='wrap'>
              <TextField
                label='From Date'
                type='date'
                size='small'
                value={startDate.toISOString().split('T')[0]}
                onChange={handleStartDateChange}
                inputProps={{ max: getMaxDateString() }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 160 }}
              />
              <TextField
                label='To Date'
                type='date'
                size='small'
                value={endDate.toISOString().split('T')[0]}
                onChange={handleEndDateChange}
                inputProps={{ max: getMaxDateString() }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 160 }}
              />
              <Button
                variant='outlined'
                color='primary'
                onClick={fetchStatistics}
                disabled={loading}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 2,
                  py: 0.75,
                  borderRadius: 1,
                  color: 'dark.main',
                  borderColor: 'dark.main',
                  '&:hover': {
                    borderColor: 'dark.main',
                    backgroundColor: 'grey.100',
                  },
                }}
              >
                {loading ? 'Loading...' : 'Fetch'}
              </Button>
            </MDBox>
          </Card>
        </MDBox>

        {error && (
          <MDBox mb={2}>
            <Card sx={{ boxShadow: 2, borderRadius: 2, p: 2, bgcolor: 'error.lighter' }}>
              <MDTypography variant='body2' color='error'>
                {error}
              </MDTypography>
            </Card>
          </MDBox>
        )}

        {loading ? (
          <MDBox py={4} textAlign='center'>
            <CircularProgress size={32} />
            <MDTypography variant='body2' color='text' mt={1}>
              Loading statistics...
            </MDTypography>
          </MDBox>
        ) : stats ? (
          <Grid container spacing={2}>
            {/* Summary Cards Row */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ boxShadow: 2, borderRadius: 2, p: 2, height: '100%' }}>
                <MDTypography variant='caption' fontWeight='medium' color='textSecondary'>
                  New Registrations
                </MDTypography>
                <MDTypography variant='h4' fontWeight='bold' color='primary'>
                  {stats.users?.newRegistrations || 0}
                </MDTypography>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ boxShadow: 2, borderRadius: 2, p: 2, height: '100%' }}>
                <MDTypography variant='caption' fontWeight='medium' color='textSecondary'>
                  Active Users
                </MDTypography>
                <MDTypography variant='h4' fontWeight='bold' color='success'>
                  {stats.users?.weeklyActiveUsers || 0}
                </MDTypography>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ boxShadow: 2, borderRadius: 2, p: 2, height: '100%' }}>
                <MDTypography variant='caption' fontWeight='medium' color='textSecondary'>
                  Total Sessions
                </MDTypography>
                <MDTypography variant='h4' fontWeight='bold' color='info'>
                  {stats.sessions?.totalSessions || 0}
                </MDTypography>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ boxShadow: 2, borderRadius: 2, p: 2, height: '100%' }}>
                <MDTypography variant='caption' fontWeight='medium' color='textSecondary'>
                  Avg Session
                </MDTypography>
                <MDTypography variant='h4' fontWeight='bold' color='warning'>
                  {formatDuration(stats.sessions?.avgSessionLengthSeconds || 0)}
                </MDTypography>
              </Card>
            </Grid>

            {/* Retention & Churn Row */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ boxShadow: 2, borderRadius: 2, p: 2, height: '100%' }}>
                <MDTypography variant='caption' fontWeight='medium' color='textSecondary'>
                  Retention Rate
                </MDTypography>
                <MDBox display='flex' alignItems='center' gap={1}>
                  <MDTypography variant='h4' fontWeight='bold'>
                    {(stats.users?.retentionRate || 0).toFixed(1)}%
                  </MDTypography>
                  <Chip
                    label={stats.users?.retentionRate > 50 ? 'Good' : 'Low'}
                    color={stats.users?.retentionRate > 50 ? 'success' : 'warning'}
                    size='small'
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                </MDBox>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ boxShadow: 2, borderRadius: 2, p: 2, height: '100%' }}>
                <MDTypography variant='caption' fontWeight='medium' color='textSecondary'>
                  Churn Rate
                </MDTypography>
                <MDBox display='flex' alignItems='center' gap={1}>
                  <MDTypography variant='h4' fontWeight='bold'>
                    {(stats.users?.churnRate || 0).toFixed(1)}%
                  </MDTypography>
                  <Chip
                    label={stats.users?.churnRate < 20 ? 'Good' : 'High'}
                    color={stats.users?.churnRate < 20 ? 'success' : 'error'}
                    size='small'
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                </MDBox>
              </Card>
            </Grid>

            {/* Games Played Section */}
            <Grid item xs={12} md={6}>
              <Card sx={{ boxShadow: 2, borderRadius: 2, p: 2, height: '100%' }}>
                <MDTypography variant='subtitle2' fontWeight='bold' color='dark' mb={1}>
                  Games Played
                </MDTypography>
                {loadingGamesPlayed ? (
                  <MDBox py={2} textAlign='center'>
                    <CircularProgress size={24} />
                  </MDBox>
                ) : gamesPlayed ? (
                  <>
                    <MDBox display='flex' alignItems='baseline' gap={1} mb={1}>
                      <MDTypography variant='h4' fontWeight='bold' color='primary'>
                        {gamesPlayed.total?.toLocaleString() || 0}
                      </MDTypography>
                      <MDTypography variant='caption' color='textSecondary'>
                        total games
                      </MDTypography>
                    </MDBox>
                    <Grid container spacing={0.5}>
                      {Object.entries(gamesPlayed.byGameType || {})
                        .filter(([, count]) => count > 0)
                        .sort(([, a], [, b]) => b - a)
                        .map(([gameType, count]) => (
                          <Grid item xs={6} key={gameType}>
                            <MDBox
                              display='flex'
                              justifyContent='space-between'
                              alignItems='center'
                              py={0.25}
                              px={0.5}
                              sx={{ backgroundColor: 'grey.50', borderRadius: 0.5 }}
                            >
                              <MDTypography variant='caption' color='textSecondary'>
                                {GAME_TYPE_NAMES[gameType] || gameType}
                              </MDTypography>
                              <MDTypography variant='caption' fontWeight='bold'>
                                {count.toLocaleString()}
                              </MDTypography>
                            </MDBox>
                          </Grid>
                        ))}
                    </Grid>
                  </>
                ) : (
                  <MDTypography variant='caption' color='textSecondary'>
                    No data
                  </MDTypography>
                )}
              </Card>
            </Grid>

            {/* Top Countries */}
            {stats.topCountries && stats.topCountries.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card sx={{ boxShadow: 2, borderRadius: 2, p: 2, height: '100%' }}>
                  <MDTypography variant='subtitle2' fontWeight='bold' color='dark' mb={1}>
                    Top Registration Countries
                  </MDTypography>
                  <Grid container spacing={0.5}>
                    {stats.topCountries.slice(0, 10).map((country, idx) => (
                      <Grid item xs={6} sm={4} key={`${country.country}-${idx}`}>
                        <MDBox
                          display='flex'
                          alignItems='center'
                          gap={0.5}
                          py={0.25}
                          px={0.5}
                          sx={{
                            backgroundColor: idx < 3 ? 'primary.lighter' : 'grey.50',
                            borderRadius: 0.5,
                          }}
                        >
                          <MDTypography
                            variant='caption'
                            color='textSecondary'
                            sx={{ minWidth: 16 }}
                          >
                            #{idx + 1}
                          </MDTypography>
                          <MDTypography
                            variant='caption'
                            fontWeight={idx < 3 ? 'bold' : 'medium'}
                            sx={{ flex: 1 }}
                          >
                            {country.country}
                          </MDTypography>
                          <MDTypography variant='caption' fontWeight='bold' color='primary'>
                            {country.count}
                          </MDTypography>
                        </MDBox>
                      </Grid>
                    ))}
                  </Grid>
                </Card>
              </Grid>
            )}

            {/* Detailed Metrics Table */}
            <Grid item xs={12}>
              <Card sx={{ boxShadow: 2, borderRadius: 2, p: 2 }}>
                <MDTypography variant='subtitle2' fontWeight='bold' color='dark' mb={1}>
                  Detailed Metrics
                </MDTypography>
                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'grey.100' }}>
                        <TableCell sx={{ py: 0.75 }}>
                          <MDTypography variant='caption' fontWeight='bold'>
                            Metric
                          </MDTypography>
                        </TableCell>
                        <TableCell align='right' sx={{ py: 0.75 }}>
                          <MDTypography variant='caption' fontWeight='bold'>
                            Value
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ py: 0.5 }}>
                          <MDTypography variant='caption'>Period</MDTypography>
                        </TableCell>
                        <TableCell align='right' sx={{ py: 0.5 }}>
                          <MDTypography variant='caption' fontWeight='medium'>
                            {formatDate(stats.periodStart)} - {formatDate(stats.periodEnd)}
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ py: 0.5 }}>
                          <MDTypography variant='caption'>Unique Users Playing</MDTypography>
                        </TableCell>
                        <TableCell align='right' sx={{ py: 0.5 }}>
                          <MDTypography variant='caption' fontWeight='medium'>
                            {stats.cryptoExpress?.uniqueUsers || 0}
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ py: 0.5 }}>
                          <MDTypography variant='caption'>Referral Rate</MDTypography>
                        </TableCell>
                        <TableCell align='right' sx={{ py: 0.5 }}>
                          <MDTypography variant='caption' fontWeight='medium'>
                            {(stats.referrals?.referralRate || 0).toFixed(1)}%
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ py: 0.5 }}>
                          <MDTypography variant='caption'>New Referrals</MDTypography>
                        </TableCell>
                        <TableCell align='right' sx={{ py: 0.5 }}>
                          <MDTypography variant='caption' fontWeight='medium'>
                            {stats.referrals?.newReferrals || 0}
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Grid>

            {/* Per-User Session Data */}
            <Grid item xs={12}>
              <Card sx={{ boxShadow: 2, borderRadius: 2, p: 2 }}>
                <MDTypography variant='subtitle2' fontWeight='bold' color='dark' mb={1}>
                  Session Length Per User
                </MDTypography>

                {loadingUserSessions ? (
                  <MDBox py={2} textAlign='center'>
                    <CircularProgress size={24} />
                  </MDBox>
                ) : userSessions.length === 0 ? (
                  <MDTypography variant='caption' color='textSecondary'>
                    No session data available
                  </MDTypography>
                ) : (
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size='small' stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ py: 0.75, backgroundColor: 'grey.100' }}>
                            <MDTypography variant='caption' fontWeight='bold'>
                              User
                            </MDTypography>
                          </TableCell>
                          <TableCell align='right' sx={{ py: 0.75, backgroundColor: 'grey.100' }}>
                            <MDTypography variant='caption' fontWeight='bold'>
                              Sessions
                            </MDTypography>
                          </TableCell>
                          <TableCell align='right' sx={{ py: 0.75, backgroundColor: 'grey.100' }}>
                            <MDTypography variant='caption' fontWeight='bold'>
                              Total Time
                            </MDTypography>
                          </TableCell>
                          <TableCell align='right' sx={{ py: 0.75, backgroundColor: 'grey.100' }}>
                            <MDTypography variant='caption' fontWeight='bold'>
                              Avg
                            </MDTypography>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {userSessions.map((userSession, index) => (
                          <TableRow
                            key={userSession.userId}
                            sx={{ backgroundColor: index % 2 === 0 ? 'white' : 'grey.25' }}
                          >
                            <TableCell sx={{ py: 0.5 }}>
                              <MDTypography variant='caption'>
                                {userSession.userNickname || userSession.userEmail || userSession.userId.slice(0, 8)}
                              </MDTypography>
                            </TableCell>
                            <TableCell align='right' sx={{ py: 0.5 }}>
                              <MDTypography variant='caption'>
                                {userSession.totalSessions}
                              </MDTypography>
                            </TableCell>
                            <TableCell align='right' sx={{ py: 0.5 }}>
                              <MDTypography variant='caption' fontWeight='medium'>
                                {formatDuration(userSession.totalSessionTime)}
                              </MDTypography>
                            </TableCell>
                            <TableCell align='right' sx={{ py: 0.5 }}>
                              <MDTypography variant='caption' fontWeight='medium' color='primary'>
                                {formatDuration(userSession.averageSessionLength)}
                              </MDTypography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Card>
            </Grid>
          </Grid>
        ) : null}
      </MDBox>
    </DashboardLayout>
  );
}
